// <EntityHeader> — shared entity detail page header per DESIGN.update.md §7.4.
//
// Renders: icon + display name + mono id (click-to-copy) + optional status
// chips + optional right-aligned actions + optional breadcrumbs.

import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { Breadcrumbs } from "./Breadcrumbs";
import type { BreadcrumbItem } from "./Breadcrumbs";
import "./EntityHeader.css";

export interface EntityHeaderProps {
  icon: ReactNode;
  displayName: string;
  idLabel: string;
  statusChips?: ReactNode[];
  actions?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

export function EntityHeader({
  icon,
  displayName,
  idLabel,
  statusChips,
  actions,
  breadcrumbs,
}: EntityHeaderProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(idLabel);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API may not be available in all contexts.
    }
  }, [idLabel]);

  return (
    <div className="rb-entity-header">
      {breadcrumbs && breadcrumbs.length >= 2 && (
        <Breadcrumbs items={breadcrumbs} />
      )}
      <div className="rb-entity-header__row">
        <div className="rb-entity-header__identity">
          <span className="rb-entity-header__icon" aria-hidden="true">
            {icon}
          </span>
          <div className="rb-entity-header__names">
            <h1 className="rb-entity-header__display-name">{displayName}</h1>
            <button
              type="button"
              className="rb-entity-header__id-label"
              onClick={handleCopyId}
              title={copied ? "Copied!" : "Click to copy"}
              aria-label={`Copy ID: ${idLabel}`}
            >
              <code>{idLabel}</code>
              <span className="rb-entity-header__copy-hint" aria-hidden="true">
                {copied ? "✓" : "⎘"}
              </span>
            </button>
          </div>
          {statusChips && statusChips.length > 0 && (
            <div className="rb-entity-header__chips">
              {statusChips.map((chip, i) => (
                <span key={i}>{chip}</span>
              ))}
            </div>
          )}
        </div>
        {actions && (
          <div className="rb-entity-header__actions">{actions}</div>
        )}
      </div>
    </div>
  );
}
